import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../api/client';
import type { EmailConfig } from '../../types';

export function EmailPage() {
  const [data, setData] = useState<EmailConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  async function reload() {
    try { setData(await api<EmailConfig>('/api/admin/email')); }
    catch (err) { setError(err instanceof Error ? err.message : 'Load failed'); }
  }
  useEffect(() => { void reload(); }, []);

  async function onSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!data) return;
    setError(null); setInfo(null); setSaving(true);
    try {
      await api('/api/admin/email', { method: 'PUT', body: JSON.stringify(data) });
      setInfo('Saved.');
      await reload();
    } catch (err) { setError(err instanceof Error ? err.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  async function onSendTest() {
    setError(null); setInfo(null); setTesting(true);
    try {
      const res = await api<{ sentTo: string }>('/api/admin/email/test', { method: 'POST' });
      setInfo(`Test email sent to ${res.sentTo}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally { setTesting(false); }
  }

  if (!data) {
    return (
      <section className="admin-page">
        {error ? <div className="form-error">{error}</div> : <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      </section>
    );
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header"><h1>Email</h1></header>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>
        SMTP credentials for transactional email (invites, password resets). Recommended: <strong>Resend</strong> (free tier 3,000/mo, 100/day). Any provider with SMTP credentials works.
      </p>
      {error && <div className="form-error">{error}</div>}
      {info && <div className="info-banner">{info}</div>}
      <form onSubmit={(e) => void onSave(e)} className="admin-form">
        <div className="form-row-pair">
          <div className="form-group">
            <label>SMTP host</label>
            <input type="text" required value={data.host} onChange={(e) => setData({ ...data, host: e.target.value })} placeholder="smtp.resend.com" />
          </div>
          <div className="form-group">
            <label>Port</label>
            <input type="number" required value={data.port} onChange={(e) => setData({ ...data, port: Number(e.target.value) })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-checkbox">
            <input type="checkbox" checked={data.secure} onChange={(e) => setData({ ...data, secure: e.target.checked })} />
            Use TLS (set for port 465; leave off for STARTTLS on 587)
          </label>
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label>Username</label>
            <input type="text" value={data.username} onChange={(e) => setData({ ...data, username: e.target.value })} placeholder="resend" />
          </div>
          <div className="form-group">
            <label>Password / API key</label>
            <input
              type="text"
              value={data.password}
              onChange={(e) => setData({ ...data, password: e.target.value })}
              placeholder={data.password === '••••••' ? 'Saved — leave as is to keep' : 'Paste API key / SMTP password'}
              autoComplete="off"
            />
          </div>
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label>From email</label>
            <input type="email" required value={data.fromEmail} onChange={(e) => setData({ ...data, fromEmail: e.target.value })} placeholder="hello@vietsurvive.com" />
          </div>
          <div className="form-group">
            <label>From name</label>
            <input type="text" value={data.fromName} onChange={(e) => setData({ ...data, fromName: e.target.value })} placeholder="VietSurvive" />
          </div>
        </div>
        <div className="form-group">
          <label className="form-checkbox">
            <input type="checkbox" checked={data.isActive} onChange={(e) => setData({ ...data, isActive: e.target.checked })} />
            Active (required to send invites + password resets)
          </label>
        </div>
        <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" className="btn-secondary" onClick={() => void onSendTest()} disabled={testing || !data.isActive}>
            {testing ? 'Sending…' : 'Send test email to me'}
          </button>
        </div>
      </form>
    </section>
  );
}
